/**
 * Auth request bodies — 04 §4.1 and §3.3.
 *
 * Response shapes live in `types.ts`; these are the bodies the API validates
 * and the two frontends build.
 */
import { z } from 'zod';
import { StudentStatus, UserRole } from '../enums.js';
import { PasswordSchema, ResetCodeSchema, UniversityIdSchema, UuidSchema } from './common.js';
/** `POST /auth/staff/login`. */
export const StaffLoginSchema = z.object({
    email: z.string().trim().email().toLowerCase(),
    password: PasswordSchema,
});
/** `POST /auth/student/login`. */
export const StudentLoginSchema = z.object({
    universityId: UniversityIdSchema,
    password: PasswordSchema,
});
/* ───────────────────── login bodies as the API validates them ───────────── */
/**
 * The password field of a *login* body.
 *
 * Deliberately weaker than {@link PasswordSchema}: the `>= 8` rule is a policy
 * for the password a principal *sets*, not for the one they already have. The
 * seeded students carry a 4-character bootstrap password with
 * `must_change_password = true` (03 §7.2), and rejecting their login with
 * `422 VALIDATION_ERROR` would make the forced-change flow unreachable.
 * Authentication still answers `401 AUTH_INVALID_CREDENTIALS` either way.
 */
export const LoginPasswordSchema = z.string().min(1).max(128);
/** `POST /auth/staff/login` as the API validates it. */
export const StaffLoginBodySchema = z.object({
    email: z.string().trim().email().toLowerCase(),
    password: LoginPasswordSchema,
});
/** `POST /auth/student/login` as the API validates it. */
export const StudentLoginBodySchema = z.object({
    universityId: UniversityIdSchema,
    password: LoginPasswordSchema,
});
/**
 * `POST /auth/{staff,student}/refresh`. The refresh token itself travels in
 * an httpOnly cookie, so the body is empty — the schema exists so the route
 * still runs through the same validation pipe.
 */
export const RefreshSchema = z.object({});
/**
 * `POST /auth/{staff,student}/change-password`.
 *
 * "must differ from the current password" is enforced by the API against the
 * stored hash and answered with `422 PASSWORD_SAME_AS_OLD`, not here — a
 * validation error would lose that distinction.
 */
export const ChangePasswordSchema = z.object({
    currentPassword: LoginPasswordSchema,
    newPassword: PasswordSchema,
});
/** `POST /auth/student/reset-password` — TA-issued one-time code. */
export const StudentResetPasswordSchema = z.object({
    universityId: UniversityIdSchema,
    code: ResetCodeSchema,
    newPassword: PasswordSchema,
});
/* ─────────────────────── access token claims (04 §3.2) ─────────────────── */
/** Audience claim: the two principals never share a token. */
export const TokenAudienceSchema = z.enum(['staff', 'student']);
/**
 * Claims of the HS256 access token. Modelled here so the frontends can decode
 * their own token for `exp`-driven refresh scheduling without a JWT library
 * that would also need the secret.
 */
export const AccessTokenClaimsSchema = z.object({
    sub: z.string().uuid(),
    aud: TokenAudienceSchema,
    typ: z.literal('access'),
    /** Staff only. */
    role: z.nativeEnum(UserRole).optional(),
    name: z.string(),
    /** Staff only: the login email. */
    email: z.string().optional(),
    /** Student only: `university_id`. */
    uid: z.string().optional(),
    /** Student only: `cohort_id`. */
    cid: z.string().optional(),
    /** Student only: `must_change_password`. */
    mcp: z.boolean().optional(),
    jti: z.string(),
    /** Seconds since the epoch, as JWT defines them. */
    iat: z.number().int(),
    exp: z.number().int(),
});
/* ──────────────────────────── the resolved actor ───────────────────────── */
/**
 * What `JwtAuthGuard` puts on `req.actor` and what `GET /auth/me` returns
 * (04 §3.4). `type` uses the `actor_type` vocabulary of the audit log so an
 * audit row can be written straight from the actor.
 */
export const StaffActorSchema = z.object({
    type: z.literal('USER'),
    id: UuidSchema,
    name: z.string(),
    email: z.string(),
    role: z.nativeEnum(UserRole),
});
/**
 * The student's own cohort (الدفعة) as the student app is allowed to see it.
 *
 * `/cohorts` is `@StaffOnly()` and stays that way, so this is the only route a
 * student has to a human-readable cohort name; without it the home screen can
 * render nothing but the raw `cohortId` UUID.
 */
export const StudentCohortSchema = z.object({
    id: UuidSchema,
    name: z.string(),
    academicYear: z.string(),
});
export const StudentActorSchema = z.object({
    type: z.literal('STUDENT'),
    id: UuidSchema,
    name: z.string(),
    universityId: z.string(),
    cohortId: UuidSchema,
    status: z.nativeEnum(StudentStatus),
    mustChangePassword: z.boolean(),
    /**
     * Resolved from the database on `POST /auth/student/login` and on
     * `GET /auth/me` — the two places the actor is *returned* to a client.
     *
     * Optional because the actor is also built from the access token alone on
     * every other request (04 §3.2 carries `cid`, not the cohort name), and an
     * in-process `req.actor` therefore has no cohort to show. Every response
     * that hands the actor to the student app fills it in.
     */
    cohort: StudentCohortSchema.optional(),
});
export const ActorSchema = z.discriminatedUnion('type', [StaffActorSchema, StudentActorSchema]);
/* ───────────────────────────── auth responses ──────────────────────────── */
// Named `*ResultSchema` rather than `*ResponseSchema` because `types.ts`
// already exports the `StaffLoginResponse` / `StudentLoginResponse` interfaces
// and the package barrel must not claim one name twice.
/** `POST /auth/staff/login`. */
export const StaffLoginResultSchema = z.object({
    accessToken: z.string(),
    expiresIn: z.number().int().positive(),
    actor: StaffActorSchema,
    /** 04 §4.1 shape, kept alongside `actor` for the dashboard. */
    user: StaffActorSchema.omit({ type: true }),
});
/** `POST /auth/student/login`. */
export const StudentLoginResultSchema = z.object({
    accessToken: z.string(),
    expiresIn: z.number().int().positive(),
    mustChangePassword: z.boolean(),
    actor: StudentActorSchema,
    /** 04 §4.1 shape, kept alongside `actor` for the student app. */
    student: z.object({
        id: UuidSchema,
        name: z.string(),
        universityId: z.string(),
        cohortId: UuidSchema,
    }),
});
/** `POST /auth/{staff,student}/refresh` and `/auth/student/change-password`. */
export const AccessTokenResultSchema = z.object({
    accessToken: z.string(),
    expiresIn: z.number().int().positive(),
});
/** `GET /auth/me`. */
export const MeResultSchema = z.object({ actor: ActorSchema });
//# sourceMappingURL=auth.js.map